import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useApp } from './AppContext';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import UpdatePrompt from './components/UpdatePrompt';

import Login from './routes/Login';
import Dashboard from './routes/Dashboard';
import Pipeline from './routes/Pipeline';
import NewLead from './routes/NewLead';
import ProjectDetail from './routes/ProjectDetail';
import SiteVisit from './routes/SiteVisit';
import ProposalBuilder from './routes/ProposalBuilder';
import AgreementComposer from './routes/AgreementComposer';
import JobTracker from './routes/JobTracker';
import InvoiceBuilder from './routes/InvoiceBuilder';
import Documents from './routes/Documents';
import ReceiptBuilder from './routes/ReceiptBuilder';
import Customers from './routes/Customers';
import CustomerDetail from './routes/CustomerDetail';
import Settings from './routes/Settings';
import EmailLog from './routes/EmailLog';
import ComposeEmail from './routes/ComposeEmail';
import AcceptInvite from './routes/AcceptInvite';
import ResetPassword from './routes/ResetPassword';
import ForgotPassword from './routes/ForgotPassword';

import PublicProposal from './routes/public/PublicProposal';
import PublicAgreement from './routes/public/PublicAgreement';
import PublicInvoice from './routes/public/PublicInvoice';

function Toast() {
  const { toastMsg } = useApp();
  // aria-live so the message is announced; the node stays mounted because a
  // live region that appears at the same moment as its text is often missed.
  return (
    <div className={`toast ${toastMsg ? 'show' : ''}`} role="status" aria-live="polite">
      {toastMsg}
    </div>
  );
}

/** Screens behind the login wall. Remembers where you were headed. */
function Private({ children }) {
  const { user, booting } = useApp();
  const location = useLocation();
  if (booting) return <Spinner label="Starting up" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <Layout>{children}</Layout>;
}

export default function App() {
  const { booting, user } = useApp();

  return (
    <>
      <Routes>
        {/* Public — a customer with a link, or a crew member setting up. */}
        <Route path="/p/:token" element={<PublicProposal />} />
        <Route path="/a/:token" element={<PublicAgreement />} />
        <Route path="/inv/:token" element={<PublicInvoice />} />
        <Route path="/accept-invite/:token" element={<AcceptInvite />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/forgot" element={<ForgotPassword />} />

        <Route
          path="/login"
          element={booting ? <Spinner label="Starting up" />
            : user ? <Navigate to="/dashboard" replace /> : <Login />}
        />

        <Route path="/dashboard" element={<Private><Dashboard /></Private>} />
        <Route path="/pipeline" element={<Private><Pipeline /></Private>} />
        <Route path="/lead/new" element={<Private><NewLead /></Private>} />
        <Route path="/documents" element={<Private><Documents /></Private>} />
        <Route path="/customers" element={<Private><Customers /></Private>} />
        <Route path="/customer/:id" element={<Private><CustomerDetail /></Private>} />
        <Route path="/settings" element={<Private><Settings /></Private>} />
        <Route path="/email/new" element={<Private><ComposeEmail /></Private>} />
        <Route path="/settings/emails" element={<Private><EmailLog /></Private>} />

        <Route path="/project/:id" element={<Private><ProjectDetail /></Private>} />
        <Route path="/project/:id/visit" element={<Private><SiteVisit /></Private>} />
        <Route path="/project/:id/proposal" element={<Private><ProposalBuilder /></Private>} />
        <Route path="/project/:id/agreement" element={<Private><AgreementComposer /></Private>} />
        <Route path="/project/:id/job" element={<Private><JobTracker /></Private>} />
        <Route path="/project/:id/receipt" element={<Private><ReceiptBuilder /></Private>} />
        <Route path="/project/:id/invoice" element={<Private><InvoiceBuilder /></Private>} />

        <Route path="/" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
        <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
      </Routes>
      <Toast />
      <UpdatePrompt />
    </>
  );
}
